import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldCheck, Printer, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { COMPLIANCE_OFFICER } from "@/config/compliance";

const LAST_UPDATED = "February 14, 2026";

export default function HipaaNoticePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              data-testid="hipaa-back-btn"
              onClick={() => navigate("/")}
              variant="ghost"
              className="text-primary hover:bg-primary/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <button
              data-testid="hipaa-brand-home"
              onClick={() => navigate("/get-started")}
              className="flex items-center gap-3"
              aria-label="Explore care types"
            >
              <img
                src="/tyvena-lockup-horizontal.png"
                alt="Tyvena"
                className="h-10 md:h-11 w-auto rounded-lg shadow-md"
              />
            </button>
          </div>
          <Button
            data-testid="hipaa-print-btn"
            onClick={() => window.print()}
            variant="outline"
            className="rounded-full font-body"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 rounded-full mb-6">
            <ShieldCheck className="w-4 h-4 text-secondary" />
            <span className="text-sm font-body font-semibold text-text-primary">HIPAA Notice</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary leading-tight mb-4">
            Where Tyvena stands on HIPAA — honestly.
          </h1>
          <p className="text-lg text-text-secondary font-body max-w-2xl leading-relaxed">
            We're building Tyvena to be safe for families sharing sensitive care information.
            Here's exactly what we protect today, what we don't yet, and how to use Tyvena
            responsibly during our interim launch.
          </p>
          <div className="text-sm text-text-secondary font-body mt-4">
            <span className="font-semibold">Last updated:</span> {LAST_UPDATED}
          </div>
        </div>

        {/* At-a-glance callout — interim posture */}
        <div className="mb-10 rounded-2xl border-l-4 border-amber-400 bg-amber-50/60 p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-heading font-bold text-text-primary mb-1">Interim launch posture</h2>
              <p className="text-sm font-body text-text-primary leading-relaxed">
                Tyvena is <strong>not yet operating under a HIPAA Business Associate Agreement (BAA)</strong> with our
                hosting provider. That means, technically, we should not be treated as a HIPAA-covered
                service. In practice we have already built most of the safeguards HIPAA requires —
                and we're working toward formal BAA-covered hosting. Until then, please read the
                guidance below.
              </p>
            </div>
          </div>
        </div>

        <article className="space-y-10 text-text-primary">
          <Section title="What HIPAA is (in 3 sentences)">
            <p>
              HIPAA (the Health Insurance Portability and Accountability Act) is a U.S. law that
              sets rules for how healthcare providers, insurers, and their business associates
              handle <strong>Protected Health Information (PHI)</strong>. It requires reasonable
              administrative, physical, and technical safeguards for PHI. Formal HIPAA compliance
              also requires a <em>Business Associate Agreement (BAA)</em> between an app and every
              vendor that touches PHI on its behalf.
            </p>
          </Section>

          <Section title="What Tyvena already protects">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 not-prose">
              {[
                {
                  title: "Encryption in transit",
                  body: "HTTPS/TLS on every page and API call.",
                },
                {
                  title: "Encryption at rest for PHI",
                  body: "Care-recipient names, signed forms, and chat with Asher are stored using Fernet (AES-128-CBC + HMAC-SHA256) with rotatable keys.",
                },
                {
                  title: "Strong password hashing",
                  body: "bcrypt with per-user salts.",
                },
                {
                  title: "Access controls",
                  body: "Every endpoint enforces role and ownership. Only you can read your care forms; only platform administrators can review platform-level data — and every one of those views is itself audited.",
                },
                {
                  title: "Session security",
                  body: "HttpOnly + Secure + SameSite cookies. Access tokens expire in 1 hour, refresh tokens in 7 days.",
                },
                {
                  title: "Brute-force protection",
                  body: "5 failed logins per IP+email triggers a 15-minute lockout.",
                },
                {
                  title: "Comprehensive audit log",
                  body: "Every sensitive action (login, signature, form send, export, delete, admin PHI view) is logged with immutable timestamp and IP.",
                },
                {
                  title: "Your data rights",
                  body: "Full data export and one-click account deletion from your Family Portal at any time.",
                },
                {
                  title: "De-identification",
                  body: "Remove your identifying info from provider records without deleting your account, whenever you want.",
                },
                {
                  title: "Signed consent on sign-up",
                  body: "Every new account acknowledges this Notice before creating a login. That acknowledgement is timestamped and versioned.",
                },
              ].map((item) => (
                <div key={item.title} className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-body font-semibold text-sm text-text-primary mb-0.5">{item.title}</div>
                    <p className="text-xs text-text-secondary font-body leading-relaxed">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="What Tyvena doesn't cover (yet)">
            <BulletList>
              <li><strong>A signed BAA with our hosting provider.</strong> This is the single biggest reason we call the current posture "interim." Once we're on BAA-covered infrastructure, that gap closes.</li>
              <li><strong>Formal HIPAA risk-assessment documentation.</strong> We follow best-practice engineering, but we haven't published a formal §164.308 risk assessment yet.</li>
              <li><strong>Vendor BAAs.</strong> Individual vendors (Stripe, Resend, MongoDB Atlas, OpenAI-via-proxy, Daily.co) each have their own posture; we're layering in signed BAAs progressively.</li>
            </BulletList>
          </Section>

          <Section title="What to avoid entering — for now">
            <p>Because we can't yet promise BAA-level protection, please <strong>do not</strong> share the following through Tyvena (chat with Asher, family portal, care recipient field, signed forms):</p>
            <BulletList>
              <li>Social Security numbers</li>
              <li>Full medical records, lab results, or imaging reports</li>
              <li>ICD-10 / diagnosis codes</li>
              <li>Insurance card numbers</li>
              <li>Prescription lists tied to a full name</li>
            </BulletList>
            <p><strong>What's absolutely fine to share:</strong></p>
            <BulletList>
              <li>First name or nickname of the person you care for</li>
              <li>Their ZIP code or city</li>
              <li>The general kind of care they need (home care, hospice, adult day, etc.)</li>
              <li>Provider preferences (Medicare-accepting, specific religions, languages, etc.)</li>
              <li>Names and contact info of providers you're already working with</li>
            </BulletList>
          </Section>

          <Section title="How to exercise your privacy rights">
            <p>All of these actions are available inside your Family Portal, without needing to email anyone:</p>
            <BulletList>
              <li><strong>Download my data</strong> — a complete JSON export of everything Tyvena has stored about you. Portal → <em>Your Data Rights</em> → <em>Download My Data</em>.</li>
              <li><strong>Delete my account</strong> — permanently wipe your profile, forms, consults, chat, notifications, and audit log. Portal → <em>Your Data Rights</em> → <em>Delete My Account</em>.</li>
              <li><strong>De-identify me from provider records</strong> — remove your name from any provider notifications while keeping your account. Portal → <em>Privacy & Sharing</em>.</li>
            </BulletList>
            <p>If our UI doesn't cover what you need, email
              <a href={`mailto:${COMPLIANCE_OFFICER.email}`} className="text-primary underline"> {COMPLIANCE_OFFICER.email} </a>
              and our Privacy Officer will respond personally.
            </p>
          </Section>

          <Section title="How we handle a possible breach">
            <p>
              If we ever discover that PHI has been accessed by someone who shouldn't have accessed it,
              we'll notify affected users by email within <strong>72 hours</strong> of confirming the incident
              (or sooner if practical), tell you exactly what happened, and detail the remediation steps taken.
              We'll do this even during our interim non-BAA period — it's the right thing to do regardless
              of legal obligation.
            </p>
          </Section>

          <Section title="What we're working toward">
            <BulletList>
              <li>Migration to BAA-covered hosting infrastructure (either an Emergent-managed HIPAA plan when available, or a customer-owned AWS deployment).</li>
              <li>Signed BAAs with each vendor that processes PHI on our behalf.</li>
              <li>Formal §164.308 risk assessment documentation.</li>
              <li>Structured PHI redaction on export & backup.</li>
            </BulletList>
            <p className="text-sm text-text-secondary italic">
              We publish updates to this page as we cross each milestone. Bookmark and check back, or email us and we'll add you to the compliance-updates list.
            </p>
          </Section>

          <Section title="Questions?">
            <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-5 my-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-body font-semibold text-text-primary">{COMPLIANCE_OFFICER.name} — {COMPLIANCE_OFFICER.title}</p>
                  <p className="font-body text-sm text-text-secondary mt-1">
                    <a href={`mailto:${COMPLIANCE_OFFICER.email}`} className="text-primary underline">{COMPLIANCE_OFFICER.email}</a>
                  </p>
                  <p className="font-body text-sm text-text-secondary">{COMPLIANCE_OFFICER.location}</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-text-secondary italic">
              For your safety, please don't include actual medical records in your first email — reach out first and we'll direct you to a secure channel if needed.
            </p>
          </Section>
        </article>

        <div className="mt-12 pt-8 border-t border-[#E2E8F0] flex flex-wrap items-center gap-3 justify-between print:hidden">
          <a href="/legal/privacy" data-testid="hipaa-to-privacy" className="text-sm font-body text-primary hover:underline">← Read our full Privacy Notice</a>
          <p className="text-xs text-text-secondary font-body">© {new Date().getFullYear()} Tyvena · A guide for the care journey</p>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="scroll-mt-24">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-text-primary mb-4">{title}</h2>
      <div className="space-y-4 font-body text-base leading-relaxed text-text-primary">
        {children}
      </div>
    </section>
  );
}

function BulletList({ children }) {
  return <ul className="list-disc list-outside ml-6 space-y-2 font-body text-base leading-relaxed">{children}</ul>;
}
